// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Space, Table, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined, DownloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchAllPages } from '../../../core/utils/fetchAllPages.ts'
import { exportTableRows } from '../../shared/table_grid_export/exportTableRows.ts'
import type { IndustryGridModel } from '../../../state_manager/data_models/industry_grid_model.ts'
import {
  fetchIndustries,
  createIndustry,
  renameIndustry,
  deleteIndustry,
  reassignIndustries,
  type IndustrySort
} from '../services/configurationService.ts'
import { ReplaceIndustryModal } from './ReplaceIndustryModal.tsx'
import IndustryAccountsPanel from './IndustryAccountsPanel.tsx'

const { Title, Text } = Typography
const PAGE_SIZE = 50
const EXPORT_PAGE_SIZE = 1000
// Mirror TableView's load-more trigger: fetch the next page when the scroll
// position is within this many px of the bottom.
const SCROLL_THRESHOLD_PX = 300

export default function IndustriesPanel() {
  const [rows, setRows] = useState<IndustryGridModel[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<IndustrySort[]>([{ key: 'industries__name', direction: 'asc' }])

  // Master-detail: when set, show the industry's accounts screen.
  const [detail, setDetail] = useState<{ id: string; name: string } | null>(null)

  // Name modal (add / rename)
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [editing, setEditing] = useState<IndustryGridModel | null>(null)
  const [nameValue, setNameValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Replace / merge modal
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [replaceInitialSources, setReplaceInitialSources] = useState<string[]>([])
  const [reassigning, setReassigning] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const token = () => getAccessToken(store.getState().auth)

  // Fetch one page. replace=true resets the list (page 1, new sort/search);
  // otherwise append (infinite scroll), de-duping by id.
  const fetchPage = useCallback(
    async (pageNum: number, replace: boolean) => {
      replace ? setLoading(true) : setLoadingMore(true)
      try {
        const filters = search.trim() ? [{ key: 'industries__name', value: search.trim() }] : []
        const result = await fetchIndustries(sorting, filters, pageNum, PAGE_SIZE, token())
        if (result.error) {
          showErrorToast(result.error)
          return
        }
        setHasMore(result.hasMore)
        setPage(pageNum)
        if (replace) {
          setRows(result.data)
        } else {
          setRows((prev) => {
            const seen = new Set(prev.map((r) => r.industries__id))
            return [...prev, ...result.data.filter((d) => !seen.has(d.industries__id))]
          })
        }
      } finally {
        replace ? setLoading(false) : setLoadingMore(false)
      }
    },
    [search, sorting]
  )

  // (Re)load page 1 whenever search or sort changes (and on mount).
  useEffect(() => {
    fetchPage(1, true)
  }, [fetchPage])

  // Export every industry the current search / sort matches, not only the
  // pages the infinite scroll has loaded so far.
  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const filters = search.trim() ? [{ key: 'industries__name', value: search.trim() }] : []
      const accessToken = token()
      const result = await fetchAllPages({
        pageSize: EXPORT_PAGE_SIZE,
        fetchPage: (pageNum, pageSize) => fetchIndustries(sorting, filters, pageNum, pageSize, accessToken)
      })
      if (result.error) {
        showErrorToast(result.error)
        return
      }
      await exportTableRows({
        name: 'Industries',
        rows: result.data,
        columns: [
          { title: 'Industry', value: (r) => r.industries__name },
          { title: 'Used by', value: (r) => r.industries__usage_count }
        ]
      })
    } finally {
      setExporting(false)
    }
  }, [search, sorting])

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore) return
    fetchPage(page + 1, false)
  }, [loadingMore, loading, hasMore, page, fetchPage])

  // Infinite scroll: watch the AntD table body's scroll position.
  useEffect(() => {
    const body = containerRef.current?.querySelector('.ant-table-body') as HTMLElement | null
    if (!body) return
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = body
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) loadMore()
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMore])

  const openCreate = useCallback(() => {
    setEditing(null)
    setNameValue('')
    setNameModalOpen(true)
  }, [])

  const openRename = useCallback((row: IndustryGridModel) => {
    setEditing(row)
    setNameValue(row.industries__name)
    setNameModalOpen(true)
  }, [])

  const submitName = useCallback(async () => {
    const name = nameValue.trim()
    if (!name) {
      showErrorToast('Industry name is required')
      return
    }
    setSubmitting(true)
    try {
      if (editing) {
        const result = await renameIndustry(editing.industries__id, name, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to rename industry')
          return
        }
        // Update in place (keeps position until a refresh re-sorts).
        setRows((prev) =>
          prev.map((r) => (r.industries__id === editing.industries__id ? { ...r, industries__name: name } : r))
        )
        toast.success('Industry renamed')
      } else {
        const result = await createIndustry(name, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to create industry')
          return
        }
        // Convention (cf. companies): show the newly-added row at the TOP until
        // the next refresh re-sorts it into place.
        const newId = (result.data as { industries__id?: string } | undefined)?.industries__id
        if (newId) {
          const newRow: IndustryGridModel = {
            industries__id: newId,
            industries__name: name,
            industries__sort_order: null,
            industries__usage_count: 0,
            industries__date_modified: new Date().toISOString()
          }
          setRows((prev) => [newRow, ...prev.filter((r) => r.industries__id !== newId)])
        }
        toast.success('Industry created')
      }
      setNameModalOpen(false)
      setEditing(null)
    } finally {
      setSubmitting(false)
    }
  }, [nameValue, editing])

  const handleDelete = useCallback(async (row: IndustryGridModel) => {
    if ((row.industries__usage_count || 0) > 0) {
      setReplaceInitialSources([row.industries__id])
      setReplaceOpen(true)
      return
    }
    setDeletingId(row.industries__id)
    try {
      const result = await deleteIndustry(row.industries__id, token())
      if (!result.success) {
        showErrorToast(result.error || 'Failed to delete industry')
        return
      }
      setRows((prev) => prev.filter((r) => r.industries__id !== row.industries__id))
      toast.success('Industry deleted')
    } finally {
      setDeletingId(null)
    }
  }, [])

  const handleReassign = useCallback(
    async (sourceIds: string[], targetId: string, deleteSources: boolean) => {
      setReassigning(true)
      try {
        const result = await reassignIndustries(sourceIds, targetId, deleteSources, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to reassign industries')
          return
        }
        const moved = (result.data as { accounts_reassigned?: number } | undefined)?.accounts_reassigned ?? 0
        toast.success(`Reassigned ${moved} account${moved === 1 ? '' : 's'}`)
        setReplaceOpen(false)
        fetchPage(1, true) // multiple rows changed — re-sort from the top
      } finally {
        setReassigning(false)
      }
    },
    [fetchPage]
  )

  const columns: ColumnsType<IndustryGridModel> = useMemo(
    () => [
      {
        title: 'Industry',
        dataIndex: 'industries__name',
        key: 'industries__name',
        sorter: true,
        sortOrder:
          sorting[0]?.key === 'industries__name' ? (sorting[0].direction === 'desc' ? 'descend' : 'ascend') : null,
        render: (name: string, record: IndustryGridModel) => (
          <Button type='link' className='!px-0 font-medium' onClick={() => setDetail({ id: record.industries__id, name })}>
            {name}
          </Button>
        )
      },
      {
        title: 'Used by',
        dataIndex: 'industries__usage_count',
        key: 'industries__usage_count',
        width: 140,
        align: 'right',
        sorter: true,
        sortOrder:
          sorting[0]?.key === 'industries__usage_count'
            ? sorting[0].direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (count: number) => (
          <Text type={count > 0 ? undefined : 'secondary'}>
            {count} account{count === 1 ? '' : 's'}
          </Text>
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_: unknown, record: IndustryGridModel) => (
          <Space size='small'>
            <Tooltip title='Rename'>
              <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openRename(record)} />
            </Tooltip>
            {(record.industries__usage_count || 0) > 0 ? (
              <Tooltip title='In use — replace to delete'>
                <Button type='text' size='small' danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
              </Tooltip>
            ) : (
              <Popconfirm
                title='Delete this industry?'
                okText='Delete'
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete(record)}
              >
                <Tooltip title='Delete'>
                  <Button
                    type='text'
                    size='small'
                    danger
                    loading={deletingId === record.industries__id}
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      }
    ],
    [sorting, openRename, handleDelete, deletingId]
  )

  const handleTableChange = useCallback((_p: unknown, _f: unknown, sorter: any) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s && s.order && s.columnKey) {
      setSorting([{ key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }])
    } else {
      setSorting([{ key: 'industries__name', direction: 'asc' }])
    }
  }, [])

  // Detail screen takes over the panel when an industry name is clicked.
  if (detail) {
    return (
      <IndustryAccountsPanel
        industryId={detail.id}
        industryName={detail.name}
        onBack={() => {
          setDetail(null)
          fetchPage(1, true) // refresh usage counts after any reassignments
        }}
      />
    )
  }

  return (
    <div className='w-full h-full flex flex-col overflow-hidden' ref={containerRef}>
      <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
        <Title level={4} style={{ margin: 0 }}>
          Industries
        </Title>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={rows.length === 0}
            onClick={() => void handleExport()}
          >
            Export
          </Button>
          <Input.Search
            placeholder='Search industries...'
            allowClear
            onSearch={(v) => setSearch(v || '')}
            style={{ width: 240 }}
          />
          <Button icon={<SwapOutlined />} onClick={() => { setReplaceInitialSources([]); setReplaceOpen(true) }}>
            Replace / Merge
          </Button>
          <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
            New Industry
          </Button>
        </Space>
      </div>

      <div className='flex-1 min-h-0'>
        <Table<IndustryGridModel>
          rowKey='industries__id'
          columns={columns}
          dataSource={rows}
          loading={loading}
          onChange={handleTableChange}
          pagination={false}
          scroll={{ y: 'calc(100vh - 320px)' }}
          size='middle'
          footer={
            loadingMore
              ? () => <div className='text-center text-gray-400'>Loading more…</div>
              : !hasMore && rows.length > 0
              ? () => <div className='text-center text-gray-400'>End of list</div>
              : undefined
          }
        />
      </div>

      <Modal
        title={editing ? 'Rename industry' : 'New industry'}
        open={nameModalOpen}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={submitting}
        onOk={submitName}
        onCancel={() => {
          setNameModalOpen(false)
          setEditing(null)
        }}
        destroyOnClose
      >
        <Input
          placeholder='Industry name'
          value={nameValue}
          maxLength={120}
          onChange={(e) => setNameValue(e.target.value)}
          onPressEnter={submitName}
          autoFocus
        />
      </Modal>

      <ReplaceIndustryModal
        open={replaceOpen}
        industries={rows}
        initialSourceIds={replaceInitialSources}
        confirmLoading={reassigning}
        onConfirm={handleReassign}
        onCancel={() => setReplaceOpen(false)}
      />
    </div>
  )
}
